import Encoding from 'encoding-japanese';

/**
 * CSV アップロード系ページ共通のヘルパー。
 *
 * 提供する 4 関数:
 *   - decodeCsvBuffer            : ファイル ArrayBuffer → 文字コード判定 → Unicode 文字列化 → BOM 除去
 *   - validateCsvHeaders         : 必須列の存在検証（不可視文字混入も検出）
 *   - extractAxiosErrorMessage   : axios エラーから人間可読なメッセージを抽出
 *   - formatHeaderValidationError: validateCsvHeaders の結果を一行メッセージに整形
 *
 * 設計意図:
 *   2026-05 の事故 (BOM 付き CSV + papaparse 5.3.1 の組み合わせで record.email が undefined になり
 *   バックエンドが matchedCount: 0 でサイレント成功した件) を受け、papaparse のバージョンに依存しない
 *   透過的な BOM 処理と、必須列の明示検証、バックエンドエラーの直接表示を共通化する。
 */

// 不可視文字（BOM、ZWSP〜ZWJ、行/段落セパレーター、NBSP、各種空白、ASCII 制御文字）の文字クラス。
// \s は ECMAScript 上で多くの空白系を含むが、エンジン差を避けるため代表的なものを明示している。
// validateCsvHeaders の suspicious 判定および stripInvisible 内部ヘルパーで使用する。
const INVISIBLE_CHAR_CLASS =
  '[\\s\\u00A0\\u200B-\\u200D\\u2028\\u2029\\uFEFF\\x00-\\x1F\\x7F]';

/**
 * 文字列から不可視文字を全て除去する内部ヘルパー。
 *
 * @param {string} s
 * @returns {string}
 */
function stripInvisible(s) {
  return (s || '').replace(new RegExp(INVISIBLE_CHAR_CLASS, 'g'), '');
}

/**
 * 1 文字が不可視文字かどうかを判定する内部ヘルパー。
 *
 * @param {string} c - 単一文字
 * @returns {boolean}
 */
function isInvisibleChar(c) {
  return new RegExp('^' + INVISIBLE_CHAR_CLASS + '$').test(c);
}

/**
 * CSV ファイルの ArrayBuffer を読み込み、文字コード自動判定 → Unicode 文字列化 → 先頭 BOM 除去を一括で行う。
 *
 * - encoding-japanese で UTF-8 / Shift-JIS / EUC-JP 等を自動判定して Unicode (UTF-16) 文字列に変換
 * - 先頭の BOM (U+FEFF) を明示的に除去（papaparse のバージョンに依存しない透過処理を保証）
 * - 本文中に偶然含まれる U+FEFF は保存する（先頭のみ除去）
 *
 * @param {ArrayBuffer} arrayBuffer - FileReader.readAsArrayBuffer の結果
 * @returns {string} BOM 除去済みの Unicode 文字列。Papa.parse にそのまま渡せる
 */
export function decodeCsvBuffer(arrayBuffer) {
  const codes = new Uint8Array(arrayBuffer);
  const encoding = Encoding.detect(codes);
  const unicodeString = Encoding.convert(codes, {
    to: 'unicode',
    from: encoding,
    type: 'string',
  });
  // 先頭の BOM (U+FEFF) のみ除去。本文中の同コードポイントは保存する
  return unicodeString.replace(/^﻿/, '');
}

/**
 * CSV ヘッダーに必須列が含まれているかを検証する。
 *
 * - 完全一致が見つかれば OK
 * - 見つからない場合、不可視文字を除去すれば一致する「類似ヘッダー」を探索
 *   → 見つかれば suspicious、見つからなければ missing
 * - BOM (U+FEFF) は decodeCsvBuffer で事前除去される想定。本関数の suspicious は
 *   それ以外の不可視文字 (先頭空白 U+0020、Tab U+0009、制御文字、ゼロ幅スペース等) を捕捉する安全網
 *
 * @param {string[]} headers - papaparse の results.meta.fields
 * @param {string[]} requiredColumns - 必須列名の配列 (例: ['email'] または ['email', 'relation'])
 * @returns {{
 *   ok: boolean,
 *   missing: string[],
 *   suspicious: Array<{ expected: string, actual: string, prefixCodes: string }>
 * }}
 */
export function validateCsvHeaders(headers, requiredColumns) {
  const safeHeaders = Array.isArray(headers) ? headers : [];
  const safeRequired = Array.isArray(requiredColumns) ? requiredColumns : [];
  const missing = [];
  const suspicious = [];

  for (const required of safeRequired) {
    if (safeHeaders.includes(required)) {
      continue; // 完全一致 — OK
    }

    // 類似ヘッダー探索: 不可視文字を除去した結果が required と一致するもの
    const requiredNormalized = required.toLowerCase();
    const found = safeHeaders.find(
      (h) =>
        typeof h === 'string' &&
        h !== required &&
        stripInvisible(h).toLowerCase() === requiredNormalized
    );

    if (found) {
      // 不可視文字のみを抽出して U+XXXX 形式で表示（運用担当が原因特定しやすいように）
      const prefixCodes = [...found]
        .filter(isInvisibleChar)
        .map(
          (c) =>
            'U+' +
            c.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')
        )
        .join(' ');
      suspicious.push({ expected: required, actual: found, prefixCodes });
    } else {
      missing.push(required);
    }
  }

  return {
    ok: missing.length === 0 && suspicious.length === 0,
    missing,
    suspicious,
  };
}

/**
 * axios のエラーオブジェクトから、ユーザーに表示する一行メッセージを抽出する。
 *
 * 抽出優先順位:
 *   1. error.response.data.error      - バックエンドルーターの標準応答フィールド
 *   2. error.response.data.message    - express-validator 等の応答
 *   3. error.message                  - axios/network レベルのエラー
 *   4. defaultMessage                 - 上記いずれも無い場合のフォールバック
 *
 * 応答 status が取れる場合は末尾に「(HTTP 500)」のように付記する（運用担当の判別補助）。
 *
 * @param {*} error - axios のエラーオブジェクト
 * @param {string} defaultMessage - フォールバック文字列
 * @returns {string} message.error の content にそのまま使える文字列
 */
export function extractAxiosErrorMessage(error, defaultMessage) {
  const fallback =
    typeof defaultMessage === 'string' && defaultMessage
      ? defaultMessage
      : 'リクエストに失敗しました。';

  const response = error && error.response;
  const data = response && response.data;
  const status = response && response.status;

  let body;
  if (data && typeof data.error === 'string' && data.error) {
    body = data.error;
  } else if (data && typeof data.message === 'string' && data.message) {
    body = data.message;
  } else if (error && typeof error.message === 'string' && error.message) {
    body = error.message;
  } else {
    body = fallback;
  }

  if (typeof status === 'number') {
    return body + ' (HTTP ' + status + ')';
  }
  return body;
}

/**
 * validateCsvHeaders の結果から、ユーザーに表示する一行メッセージを組み立てる。
 *
 * - suspicious (不可視文字が混入している類似ヘッダー) を最優先で報告
 * - missing のみの場合は単純な「X 列がありません」 メッセージ
 *
 * @param {{ ok: boolean, missing: string[], suspicious: Array<{ expected: string, actual: string, prefixCodes: string }> }} validationResult
 * @returns {string} message.error の content にそのまま使える日本語メッセージ
 */
export function formatHeaderValidationError(validationResult) {
  if (!validationResult || validationResult.ok) {
    return '';
  }

  const { missing, suspicious } = validationResult;

  if (suspicious && suspicious.length > 0) {
    // 不可視文字を含むヘッダーを優先報告
    const parts = suspicious
      .map(
        (s) =>
          '「' +
          s.expected +
          '」列に不可視文字が含まれています（' +
          s.prefixCodes +
          '）'
      )
      .join('、');
    return (
      'CSVヘッダーの' +
      parts +
      '。UTF-8（BOMなし）で再保存するか、ヘッダー前後の空白等を取り除いてください。'
    );
  }

  if (missing && missing.length > 0) {
    const list = missing.map((m) => '「' + m + '」').join('・');
    return (
      'CSVに必須項目' +
      list +
      '列がありません。ヘッダー行を確認してください。'
    );
  }

  return 'CSVヘッダーの検証に失敗しました。';
}
